/**
 * WhitelabelTopBar — slim black utility bar for whitelabel vendor sites.
 *
 * Rendered ONLY on whitelabel hosts (e.g. taxassistoxley.au) — replaces
 * the platform-branded <GoogleTranslateBar/> so vendors get the language
 * picker + live clock + light/dark toggle WITHOUT any HustleSuite
 * chrome (no Modules dropdown, no Emergency menu, no account link, no
 * "Read this site in" platform prefix).
 *
 * Layout (matches user spec from screenshot):
 *   [ 🌐 Language ▾ ]                          [ Wed 12 Feb · 2:14 pm ] [ ☀︎ ]
 *
 * Uses the same Google Translate cookie approach as the main bar so
 * translations persist across page loads and match what the widget
 * actually renders.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X, Languages } from "lucide-react";
import Tip from "@/components/Tip";
import ThemeToggle from "@/components/ThemeToggle";

// ── Language catalogue — full 41-language set (mirrors GoogleTranslateBar).
const LANGS = [
  { code: "en",    label: "English",           en_name: "English",              flag: "gb" },
  { code: "zh-CN", label: "中文 (简体)",         en_name: "Chinese (Simplified)", flag: "cn" },
  { code: "zh-TW", label: "中文 (繁體)",         en_name: "Chinese (Traditional)", flag: "hk" },
  { code: "es",    label: "Español",           en_name: "Spanish",              flag: "es" },
  { code: "ar",    label: "العربية",             en_name: "Arabic",               flag: "sa" },
  { code: "hi",    label: "हिन्दी",              en_name: "Hindi",                flag: "in" },
  { code: "pt",    label: "Português",         en_name: "Portuguese",           flag: "pt" },
  { code: "bn",    label: "বাংলা",              en_name: "Bengali",              flag: "bd" },
  { code: "ru",    label: "Русский",           en_name: "Russian",              flag: "ru" },
  { code: "ja",    label: "日本語",             en_name: "Japanese",             flag: "jp" },
  { code: "pa",    label: "ਪੰਜਾਬੀ",           en_name: "Punjabi",              flag: "in" },
  { code: "de",    label: "Deutsch",           en_name: "German",               flag: "de" },
  { code: "fr",    label: "Français",          en_name: "French",               flag: "fr" },
  { code: "ko",    label: "한국어",             en_name: "Korean",               flag: "kr" },
  { code: "tr",    label: "Türkçe",            en_name: "Turkish",              flag: "tr" },
  { code: "vi",    label: "Tiếng Việt",         en_name: "Vietnamese",           flag: "vn" },
  { code: "it",    label: "Italiano",          en_name: "Italian",              flag: "it" },
  { code: "id",    label: "Bahasa Indonesia",  en_name: "Indonesian",           flag: "id" },
  { code: "th",    label: "ไทย",                en_name: "Thai",                 flag: "th" },
  { code: "pl",    label: "Polski",            en_name: "Polish",               flag: "pl" },
  { code: "uk",    label: "Українська",        en_name: "Ukrainian",            flag: "ua" },
  { code: "nl",    label: "Nederlands",        en_name: "Dutch",                flag: "nl" },
  { code: "el",    label: "Ελληνικά",           en_name: "Greek",                flag: "gr" },
  { code: "he",    label: "עברית",              en_name: "Hebrew",               flag: "il" },
  { code: "sv",    label: "Svenska",           en_name: "Swedish",              flag: "se" },
  { code: "no",    label: "Norsk",             en_name: "Norwegian",            flag: "no" },
  { code: "da",    label: "Dansk",             en_name: "Danish",               flag: "dk" },
  { code: "fi",    label: "Suomi",             en_name: "Finnish",              flag: "fi" },
  { code: "cs",    label: "Čeština",           en_name: "Czech",                flag: "cz" },
  { code: "ro",    label: "Română",            en_name: "Romanian",             flag: "ro" },
  { code: "hu",    label: "Magyar",            en_name: "Hungarian",            flag: "hu" },
  { code: "ta",    label: "தமிழ்",             en_name: "Tamil",                flag: "in" },
  { code: "te",    label: "తెలుగు",           en_name: "Telugu",               flag: "in" },
  { code: "ur",    label: "اردو",              en_name: "Urdu",                 flag: "pk" },
  { code: "fa",    label: "فارسی",             en_name: "Persian",              flag: "ir" },
  { code: "ms",    label: "Bahasa Melayu",     en_name: "Malay",                flag: "my" },
  { code: "tl",    label: "Tagalog",           en_name: "Filipino",             flag: "ph" },
  { code: "sw",    label: "Kiswahili",         en_name: "Swahili",              flag: "ke" },
  { code: "af",    label: "Afrikaans",         en_name: "Afrikaans",            flag: "za" },
  { code: "am",    label: "አማርኛ",             en_name: "Amharic",              flag: "et" },
  { code: "si",    label: "සිංහල",             en_name: "Sinhala",              flag: "lk" },
];

function _writeGoogtrans(code) {
  if (typeof document === "undefined") return;
  const expired = "Thu, 01 Jan 1970 00:00:00 GMT";
  const host = window.location.hostname;
  const bases = new Set([host]);
  const parts = host.split(".");
  if (parts.length >= 2) bases.add(parts.slice(-2).join("."));
  for (const h of bases) {
    document.cookie = `googtrans=;path=/;expires=${expired}`;
    document.cookie = `googtrans=;path=/;domain=${h};expires=${expired}`;
    document.cookie = `googtrans=;path=/;domain=.${h};expires=${expired}`;
  }
  if (code && code !== "en") {
    const value = `/en/${code}`;
    for (const h of bases) {
      document.cookie = `googtrans=${value};path=/`;
      document.cookie = `googtrans=${value};path=/;domain=${h}`;
      document.cookie = `googtrans=${value};path=/;domain=.${h}`;
    }
  }
}
function _readGoogtrans() {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)googtrans=\/[^/]+\/([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

// ── LiveClock (same format as GoogleTranslateBar) ────────────────
function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  const fmt = now
    .toLocaleString("en-AU", {
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    })
    .replace(",", "");
  const pretty = fmt.replace(", ", " · ").replace(",", " ·");
  return (
    <span
      className="inline-flex items-center gap-2 text-[12px] text-white/80 notranslate font-medium tabular-nums"
      data-testid="whitelabel-clock"
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#5DD39E] animate-pulse" />
      {pretty}
    </span>
  );
}

export default function WhitelabelTopBar() {
  const cookieLang = _readGoogtrans();
  const activeLang = cookieLang || "en";
  const active = LANGS.find((l) => l.code === activeLang) || LANGS[0];

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const inputRef = useRef(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 6, left: r.left });
    setTimeout(() => inputRef.current?.focus(), 40);
  }, [open]);

  const pick = (code) => {
    _writeGoogtrans(code);
    // Trigger Google Translate widget if present
    const combo = document.querySelector("select.goog-te-combo");
    if (combo) {
      combo.value = code === "en" ? "" : code;
      combo.dispatchEvent(new Event("change", { bubbles: true }));
      setOpen(false);
      setQ("");
      return;
    }
    // Fall back to reload — Google Translate picks up the new cookie
    setOpen(false);
    setQ("");
    try { window.location.reload(); } catch { /* ignore */ }
  };

  const results = q
    ? LANGS.filter((l) =>
        l.label.toLowerCase().includes(q.toLowerCase())
        || l.code.toLowerCase().includes(q.toLowerCase())
      )
    : [];
  const restOfList = LANGS.filter((l) => l.code !== "en");

  return (
    <div
      data-testid="whitelabel-top-bar"
      className="sticky top-0 z-[70] bg-[#0E0F0E] text-[#FAF5E8] border-b border-white/10 dark:bg-[#0E0F0E] dark:text-[#FAF5E8] dark:border-white/10 [.light_&]:bg-white [.light_&]:text-slate-900 [.light_&]:border-slate-200 notranslate"
    >
      <div className="max-w-7xl mx-auto pl-3 pr-2 sm:px-6 lg:px-8 py-1 flex items-center gap-2 sm:gap-3 flex-nowrap text-[12px]">
        {/* Left: Language picker */}
        <Languages className="h-3.5 w-3.5 text-[#F5C84B] shrink-0" />
        <span className="font-medium hidden sm:inline whitespace-nowrap">
          Read this site in:
        </span>

        <div className="relative shrink-0" ref={ref}>
          <Tip label="Click here to select your language">
            <button
              ref={btnRef}
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="whitelabel-language-toggle"
              className="inline-flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 px-2 py-0.5 text-[12px] font-medium transition"
            >
              <img
                src={`https://flagcdn.com/w20/${active.flag}.png`}
                alt=""
                className="h-3 w-auto rounded-sm"
              />
              <span className="hidden sm:inline">{active.label}</span>
              <ChevronDown className="h-3 w-3" />
            </button>
          </Tip>

          {open && createPortal(
            <div
              role="listbox"
              data-testid="whitelabel-language-menu"
              style={{
                position: "fixed",
                top: `${menuPos.top}px`,
                left: `${menuPos.left}px`,
                width: "300px",
                zIndex: 2147483000,
              }}
              className="rounded-xl bg-white text-[#0E0F0E] shadow-2xl border border-black/10 overflow-hidden notranslate"
            >
              <div className="border-b border-slate-200">
                <div className="flex items-center gap-2 px-3 py-2">
                  <Search size={14} className="text-slate-500 shrink-0" />
                  <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && results.length) pick(results[0].code);
                      else if (e.key === "Escape") { setOpen(false); setQ(""); }
                    }}
                    placeholder="Type your language…"
                    className="w-full text-[13px] bg-transparent outline-none placeholder:text-slate-500"
                    autoComplete="off"
                    spellCheck="false"
                    data-testid="whitelabel-lang-search"
                  />
                  {q && (
                    <button
                      onClick={() => { setQ(""); inputRef.current?.focus(); }}
                      className="text-slate-400 hover:text-slate-700"
                      aria-label="Clear"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              {q ? (
                <div className="max-h-72 overflow-auto py-1">
                  {results.length === 0 && (
                    <div className="px-3 py-3 text-[13px] text-slate-500">
                      No match. Try the English name.
                    </div>
                  )}
                  {results.map((l) => (
                    <LangRow key={l.code} l={l} active={active} pick={pick} />
                  ))}
                </div>
              ) : (
                <>
                  <LangRow l={LANGS[0]} active={active} pick={pick} pinned />
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400">
                    All {LANGS.length - 1} other languages
                  </div>
                  <div className="max-h-[380px] overflow-auto">
                    {restOfList.map((l) => (
                      <LangRow key={l.code} l={l} active={active} pick={pick} />
                    ))}
                  </div>
                </>
              )}
            </div>,
            document.body,
          )}
        </div>

        {/* Right: clock + light/dark toggle */}
        <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
          <span className="hidden sm:inline-flex"><LiveClock /></span>
          <Tip label="Toggle light / dark">
            <ThemeToggle
              size={12}
              testid="whitelabel-theme-toggle"
              className="!h-5 !w-5 !border-0 bg-white/15 hover:bg-white/25 text-[#FAF5E8] dark:bg-white/15 shrink-0"
            />
          </Tip>
        </div>

        {/* Hidden Google Translate mount point — required for
            select.goog-te-combo to exist so pick() can fire the
            change event without a full reload. */}
        <div id="google_translate_element" style={{ display: "none" }} />
      </div>
    </div>
  );
}

function LangRow({ l, active, pick, pinned }) {
  const isActive = l.code === active.code;
  const nativeSameAsEnglish = l.label === l.en_name;
  return (
    <button
      type="button"
      onClick={() => pick(l.code)}
      className={`w-full flex items-center gap-2 text-left px-3 py-1.5 hover:bg-emerald-50 ${
        isActive ? "bg-emerald-50" : ""
      } ${pinned ? "border-b border-slate-100" : ""}`}
      data-testid={`whitelabel-lang-option-${l.code}`}
      role="option"
      aria-selected={isActive}
    >
      <img
        src={`https://flagcdn.com/w20/${l.flag}.png`}
        alt=""
        className="h-3 w-auto rounded-sm shrink-0"
      />
      <span
        className={`text-[13px] text-slate-900 ${
          isActive ? "font-semibold" : "font-medium"
        }`}
      >
        {l.label}
      </span>
      {!nativeSameAsEnglish && (
        <span className="ml-auto text-right text-[12px] font-medium text-slate-600">
          {l.en_name}
        </span>
      )}
    </button>
  );
}
